/**
 * Creator Pro entitlements + locked-feature style (Phase B gating, Jul 2026).
 *
 * One shared, cached fetch of /api/me/entitlements powers every gated control
 * across the app. `useEntitlements()` returns `owns(sku)` / `locked(sku)` +
 * the admin-selected `lockStyle`. Cache is cleared on purchase return (the app
 * reloads after Stripe anyway), or via `refreshEntitlements()`.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export const LOCK_STYLE_OPTIONS = [
  { value: "red_shimmer", label: "Mild red shimmer", hint: "Subtle red sheen (default)." },
  { value: "amber_shimmer", label: "Warm amber shimmer", hint: "Gentler, gold-toned sheen." },
  { value: "red_static", label: "Static red sheen (frozen)", hint: "The red shimmer, frozen — no motion." },
  { value: "amber_static", label: "Static amber sheen (frozen)", hint: "The amber shimmer, frozen — no motion." },
  { value: "gray_dim", label: "Dimmed / grayed out", hint: "Fades + desaturates. Calmest, most common in SaaS." },
  { value: "lock_badge", label: "Lock badge", hint: "Normal look with a small lock icon in the corner." },
  { value: "blur", label: "Frosted blur", hint: "Soft blur + slight dim." },
  { value: "none", label: "No treatment", hint: "No visual change — still gated on click." },
];

export const lockStyleLabel = (v) =>
  LOCK_STYLE_OPTIONS.find((o) => o.value === v)?.label || v;

const SAFE_DEFAULT = {
  is_creator_pro: false,
  all_access: false,
  owned: {},
  lock_style: "red_shimmer",
  lock_styles: ["red_shimmer"],
  is_active_subscriber: false,
};

let _cache = null;
let _inflight = null;

export async function getEntitlements(force = false) {
  if (_cache && !force) return _cache;
  if (_inflight && !force) return _inflight;
  _inflight = api
    .get("/me/entitlements")
    .then((r) => {
      _cache = r.data || SAFE_DEFAULT;
      return _cache;
    })
    .catch(() => SAFE_DEFAULT)
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

export function refreshEntitlements() {
  _cache = null;
  return getEntitlements(true);
}

export function useEntitlements() {
  const [ent, setEnt] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);
  useEffect(() => {
    let alive = true;
    getEntitlements().then((e) => {
      if (alive) {
        setEnt(e);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, []);
  const owns = (sku) => !!ent && (ent.all_access || !!ent.owned?.[sku]);
  // Only "locked" once entitlements are known — avoids a lock flash on load.
  const locked = (sku) => !!ent && !owns(sku);
  // Jul 2026 (Randy) — the locked look can STACK several styles. `lockStyle`
  // now carries the full array (LockGate + tabs accept a string OR an array).
  const lockStyles = (ent?.lock_styles && ent.lock_styles.length)
    ? ent.lock_styles
    : [ent?.lock_style || "red_shimmer"];
  return { ent, loading, owns, locked, lockStyle: lockStyles, lockStyles };
}
